import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { clearElevatedAuth } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';

/**
 * Sets a single setTimeout aligned to `expiresAt`.
 * When the timer fires it dispatches clearElevatedAuth() so the gate
 * re-renders and shows the re-verification modal.
 * Clears and resets the timer whenever expiresAt changes.
 * No-op when expiresAt is null or already in the past.
 */
function useElevatedAuthExpiryWatcher(expiresAt: string | null): void {
  const dispatch = useDispatch();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!expiresAt) {
      return;
    }

    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();

    if (msUntilExpiry <= 0) {
      // Already expired — clear immediately
      dispatch(clearElevatedAuth());
      return;
    }

    timerRef.current = setTimeout(() => {
      dispatch(clearElevatedAuth());
    }, msUntilExpiry);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [expiresAt, dispatch]);
}

export default useElevatedAuthExpiryWatcher;
